import React, { useMemo, useState } from "react"
import { faAlignLeft, faClose, faLayerGroup, faPenToSquare, faPlus, faSortNumericDown } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { SimpleToggle } from "../../components/SimpleToggle/SimpleToggle"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { INPUT_SIZE } from "../../configuration/InputSize"
import { EAccountPlansType } from "../../Enum/EAccountPlansType"
import { AdminPlan } from "../../Objects/AdminPlan/AdminPlan"
import { AdminCreatePlan } from "../../Objects/AdminPlan/AdminCreatePlan"
import { AdminUpdatePlan } from "../../Objects/AdminPlan/AdminUpdatePlan"

interface AdminPlanFormPopupProps {
  plan: AdminPlan | null
  existingPlans: AdminPlan[]
  onClose: () => void
  onCreate: (request: AdminCreatePlan) => void
  onUpdate: (planId: number, request: AdminUpdatePlan) => void
}

export const AdminPlanFormPopup: React.FC<AdminPlanFormPopupProps> = ({
  plan,
  existingPlans,
  onClose,
  onCreate,
  onUpdate,
}) => {
  const { t } = useTranslation("frontend")

  const isEditing = plan !== null

  const availablePlanTypeOptions = useMemo(() => {
    const usedTypes = new Set(existingPlans.map((item) => item.eAccountPlansType))

    return Object.values(EAccountPlansType)
      .filter((value): value is EAccountPlansType => typeof value === "number")
      .filter((value) => isEditing || !usedTypes.has(value))
      .map((value) => ({ value: String(value), label: (value as number).toEAccountPlansTypeToString() }))
  }, [existingPlans, isEditing])

  const [eAccountPlansType, setEAccountPlansType] = useState<EAccountPlansType>(() => {
    if (plan) return plan.eAccountPlansType

    const firstNonFreeOption = availablePlanTypeOptions.find(
      (option) => Number(option.value) !== EAccountPlansType.None,
    )

    return Number((firstNonFreeOption ?? availablePlanTypeOptions[0])?.value ?? EAccountPlansType.None) as EAccountPlansType
  })

  const [name, setName] = useState(plan?.name ?? "")

  const [description, setDescription] = useState(plan?.description ?? "")

  const [displayOrder, setDisplayOrder] = useState(String(plan?.displayOrder ?? existingPlans.length))

  const [isActive, setIsActive] = useState(plan?.isActive ?? true)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const parsedDisplayOrder = Number.parseInt(displayOrder, 10)

    if (isEditing) {
      onUpdate(
        plan.id,
        new AdminUpdatePlan({
          name,
          description: description.trim() ? description : undefined,
          displayOrder: Number.isFinite(parsedDisplayOrder) ? parsedDisplayOrder : 0,
          isActive,
        }),
      )

      return
    }

    onCreate(
      new AdminCreatePlan({
        eAccountPlansType,
        name,
        description: description.trim() ? description : undefined,
        displayOrder: Number.isFinite(parsedDisplayOrder) ? parsedDisplayOrder : 0,
      }),
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={
                isEditing
                  ? t("admin_dashboard.plans_and_coupons.plan_edit_title")
                  : t("admin_dashboard.plans_and_coupons.plan_create_title")
              }
              icon={isEditing ? faPenToSquare : faPlus}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanFormPopup.button.onClose"
                />
              }
            />

            <form onSubmit={handleSubmit} className="mt-3 space-y-3">
              <div className="grid grid-cols-1 gap-3 p-2">
                {isEditing ? (
                  <InputField
                    type="text"
                    disable
                    placeholder={t("admin_dashboard.plans_and_coupons.plan_type")}
                    icon={faLayerGroup}
                    value={plan.eAccountPlansType.toEAccountPlansTypeToString()}
                    onChange={() => undefined}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanFormPopup.input.planType"
                  />
                ) : (
                  <GenericDropDown
                    data={availablePlanTypeOptions}
                    initialState={String(eAccountPlansType)}
                    handlerFuncion={(event) => setEAccountPlansType(Number(event.target.value) as EAccountPlansType)}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanFormPopup.select.planType"
                  />
                )}

                <InputField
                  type="text"
                  placeholder={t("admin_dashboard.plans_and_coupons.plan_name")}
                  onInvalidMessage={t("admin_dashboard.plans_and_coupons.plan_name_invalid")}
                  icon={faLayerGroup}
                  value={name}
                  required
                  maxLength={INPUT_SIZE.ADMIN_PLAN.name}
                  onChange={(event) => setName(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanFormPopup.input.name"
                />

                <InputField
                  type="text"
                  placeholder={t("admin_dashboard.plans_and_coupons.plan_description")}
                  icon={faAlignLeft}
                  value={description}
                  maxLength={INPUT_SIZE.ADMIN_PLAN.description}
                  onChange={(event) => setDescription(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanFormPopup.input.description"
                />

                <InputField
                  type="number"
                  placeholder={t("admin_dashboard.plans_and_coupons.plan_display_order")}
                  onInvalidMessage={t("admin_dashboard.plans_and_coupons.plan_display_order_invalid")}
                  icon={faSortNumericDown}
                  value={displayOrder}
                  required
                  min={0}
                  step="1"
                  onChange={(event) => setDisplayOrder(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanFormPopup.input.displayOrder"
                />

                {isEditing && (
                  <SimpleToggle
                    value={isActive}
                    title={t("admin_dashboard.plans_and_coupons.plan_is_active")}
                    onChange={setIsActive}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanFormPopup.toggle.isActive"
                  />
                )}
              </div>

              <div className="flex flex-col-reverse gap-2 p-2 md:flex-row md:justify-end">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                  onClick={onClose}
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.adminPlanFormPopup.button.cancel"
                >
                  {t("admin_dashboard.plans_and_coupons.cancel")}
                </button>

                <button
                  type="submit"
                  className="rounded-lg border border-orange-500 bg-orange-600 px-4 py-2 text-sm font-bold text-white hover:bg-orange-700"
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.adminPlanFormPopup.button.save"
                >
                  {t("admin_dashboard.plans_and_coupons.save")}
                </button>
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
